import { useEffect, useState } from "react";
import { Loader2, Clock, Save, Plus, Trash2 } from "lucide-react";
import { Button } from "../../../../ui/button";
import { Input } from "../../../..//ui/input";
import { Label } from "../../../..//ui/label";
import { Switch } from "../../../..//ui/switch";
import { supabaseReservation } from "../../../../../integrations/supabase/client";
import { useToast } from "../../../../../hooks/use-toast";
import { useAuth } from "../../../../../contexts/AuthContext";

interface TimeSlot {
  id: string;
  day_type: string;
  start_time: string;
  end_time: string;
  slot_duration: number;
  max_guests_per_slot: number;
  is_active: boolean;
  outlet_id: string | null;
}

interface NewSlot {
  day_type: string;
  start_time: string;
  end_time: string;
  slot_duration: number;
  max_guests_per_slot: number;
}

interface TimeSlotManagerProps {
  outletId?: string;
}

export function TimeSlotManager({ outletId }: TimeSlotManagerProps) {
  const { toast } = useToast();
  const { accountId } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [slots, setSlots] = useState<TimeSlot[]>([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newSlot, setNewSlot] = useState<NewSlot>({
    day_type: "weekday",
    start_time: "17:00",
    end_time: "22:00",
    slot_duration: 30,
    max_guests_per_slot: 20,
  });

  useEffect(() => {
    fetchTimeSlots();
  }, []);

  const fetchTimeSlots = async () => {

    let query = supabaseReservation
      .from("time_slots")
      .select("*");
    
    // Filter by outlet if provided
    if (outletId) {
      query = query.eq("outlet_id", outletId);
    }
    
    const { data, error } = await query
      .order("day_type")
      .order("start_time");

    if (error) {
      console.error("Error fetching time slots:", error);
    } else {
      setSlots(data || []);
    }
    setLoading(false);
  };

  const handleUpdateSlot = (id: string, field: keyof TimeSlot, value: any) => {
    setSlots(slots.map(slot => 
      slot.id === id ? { ...slot, [field]: value } : slot
    ));
  };

  const handleSave = async () => {
    setSaving(true);

    try {
      for (const slot of slots) {
        await supabaseReservation
          .from("time_slots")
          .update({
            start_time: slot.start_time,
            end_time: slot.end_time,
            slot_duration: slot.slot_duration,
            max_guests_per_slot: slot.max_guests_per_slot,
            is_active: slot.is_active,
          })
          .eq("id", slot.id);
      }

      toast({
        title: "Time Slots Saved",
        description: "Your booking availability has been updated",
      });
    } catch (error) {
      console.error("Error saving time slots:", error);
      toast({
        title: "Error",
        description: "Failed to save time slots",
        variant: "destructive",
      });
    }

    setSaving(false);
  };

  const handleAddSlot = async () => {
    setSaving(true);

    const { data, error } = await supabaseReservation
      .from("time_slots")
      .insert({
        day_type: newSlot.day_type,
        start_time: newSlot.start_time,
        end_time: newSlot.end_time,
        slot_duration: newSlot.slot_duration,
        max_guests_per_slot: newSlot.max_guests_per_slot,
        is_active: true,
        outlet_id: outletId || null,
        account_id: accountId,
      })
      .select()
      .single();

    if (error) {
      console.error("Error adding time slot:", error);
      toast({
        title: "Error",
        description: "Failed to add time slot",
        variant: "destructive",
      });
    } else {
      toast({
        title: "Time Slot Added",
        description: `New ${newSlot.day_type} slot created`,
      });
      setSlots([...slots, data]);
      setShowAddForm(false);
      setNewSlot({
        day_type: "weekday",
        start_time: "17:00",
        end_time: "22:00",
        slot_duration: 30,
        max_guests_per_slot: 20,
      });
    }

    setSaving(false);
  };

  const handleDeleteSlot = async (id: string) => {
    const { error } = await supabaseReservation
      .from("time_slots")
      .delete()
      .eq("id", id);

    if (error) {
      console.error("Error deleting time slot:", error);
      toast({
        title: "Error",
        description: "Failed to delete time slot",
        variant: "destructive",
      });
    } else {
      toast({
        title: "Time Slot Deleted",
        description: "The time slot has been removed",
      });
      setSlots(slots.filter(slot => slot.id !== id));
    }
  };

  const formatTimeForInput = (time: string) => {
    return time.substring(0, 5);
  };

  const weekdaySlots = slots.filter(s => s.day_type === "weekday");
  const weekendSlots = slots.filter(s => s.day_type === "weekend");

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  const renderSlotCard = (slot: TimeSlot) => (
    <div key={slot.id} className="p-4 rounded-lg bg-card border border-border space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Label className="text-sm text-muted-foreground">Active</Label>
          <Switch
            checked={slot.is_active}
            onCheckedChange={(checked) => handleUpdateSlot(slot.id, "is_active", checked)}
          />
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => handleDeleteSlot(slot.id)}
          className="text-destructive hover:text-destructive"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="space-y-2">
          <Label className="text-sm">Start Time</Label>
          <Input
            type="time"
            value={formatTimeForInput(slot.start_time)}
            onChange={(e) => handleUpdateSlot(slot.id, "start_time", e.target.value)}
            className="bg-background"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-sm">End Time</Label>
          <Input
            type="time"
            value={formatTimeForInput(slot.end_time)}
            onChange={(e) => handleUpdateSlot(slot.id, "end_time", e.target.value)}
            className="bg-background"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-sm">Duration (min)</Label>
          <Input
            type="number"
            min={15}
            max={120}
            step={15}
            value={slot.slot_duration}
            onChange={(e) => handleUpdateSlot(slot.id, "slot_duration", parseInt(e.target.value) || 30)}
            className="bg-background"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-sm">Max Guests</Label>
          <Input
            type="number"
            min={1}
            max={100}
            value={slot.max_guests_per_slot}
            onChange={(e) => handleUpdateSlot(slot.id, "max_guests_per_slot", parseInt(e.target.value) || 20)}
            className="bg-background"
          />
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Weekday Settings */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Clock className="h-4 w-4 text-primary" />
          <h4 className="font-medium text-foreground">Weekdays (Mon-Fri)</h4>
        </div>
        
        {weekdaySlots.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4 bg-background rounded-lg border border-border">
            No weekday slots configured
          </p>
        ) : (
          <div className="space-y-3">
            {weekdaySlots.map(renderSlotCard)}
          </div>
        )}
      </div>

      {/* Weekend Settings */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Clock className="h-4 w-4 text-primary" />
          <h4 className="font-medium text-foreground">Weekends (Sat-Sun)</h4>
        </div>
        
        {weekendSlots.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4 bg-background rounded-lg border border-border">
            No weekend slots configured
          </p>
        ) : (
          <div className="space-y-3">
            {weekendSlots.map(renderSlotCard)}
          </div>
        )}
      </div>

      {/* Add New Slot Form */}
      {showAddForm ? (
        <div className="p-4 rounded-lg bg-background border-2 border-primary/20 space-y-4">
          <h4 className="font-medium text-foreground">Add New Time Slot</h4>
          
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
            <div className="space-y-2">
              <Label className="text-sm">Day Type</Label>
              <select
                value={newSlot.day_type}
                onChange={(e) => setNewSlot({ ...newSlot, day_type: e.target.value })}
                className="w-full h-10 px-3 rounded-md border border-input bg-background text-foreground"
              >
                <option value="weekday">Weekday</option>
                <option value="weekend">Weekend</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label className="text-sm">Start Time</Label>
              <Input
                type="time"
                value={newSlot.start_time}
                onChange={(e) => setNewSlot({ ...newSlot, start_time: e.target.value })}
                className="bg-card"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm">End Time</Label>
              <Input
                type="time"
                value={newSlot.end_time}
                onChange={(e) => setNewSlot({ ...newSlot, end_time: e.target.value })}
                className="bg-card"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm">Duration</Label>
              <Input
                type="number"
                min={15}
                max={120}
                step={15}
                value={newSlot.slot_duration}
                onChange={(e) => setNewSlot({ ...newSlot, slot_duration: parseInt(e.target.value) || 30 })}
                className="bg-card"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm">Max Guests</Label>
              <Input
                type="number"
                min={1}
                max={100}
                value={newSlot.max_guests_per_slot}
                onChange={(e) => setNewSlot({ ...newSlot, max_guests_per_slot: parseInt(e.target.value) || 20 })}
                className="bg-card"
              />
            </div>
          </div>

          <div className="flex gap-2">
            <Button onClick={handleAddSlot} disabled={saving} className="flex-1">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
              Add Slot
            </Button>
            <Button variant="outline" onClick={() => setShowAddForm(false)}>
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <Button 
          variant="outline" 
          onClick={() => setShowAddForm(true)} 
          className="w-full"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add New Time Slot
        </Button>
      )}

      {slots.length > 0 && (
        <Button onClick={handleSave} disabled={saving} className="w-full">
          {saving ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="h-4 w-4 mr-2" />
              Save All Changes
            </>
          )}
        </Button>
      )}
    </div>
  );
}
